import { exists, UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { getPrimaryButtonStyling } from 'base/styleguide/concept';
import { CardStyle, UiStyle, UiStyleRadiusOption } from 'base/styleguide/model';
import { CompiledScheme } from 'base/styleguide/types';
import {
  getUiStyleComponentSlug,
  reverseNeutralColorVariable,
  shouldStyleBeAlternate,
  UI_STYLE_RADIUS_OVERRIDES_PIXELS,
  UI_STYLE_RADIUS_PIXELS,
  UI_STYLE_STYLED_CLASSES,
} from 'base/styleguide/ui_styles';
import {
  IconRadius0,
  IconRadius1,
  IconRadius2,
  IconRadius3,
} from 'base/ui/icons';
import { fromNested, Nested, toNested } from 'base/webflow/nested';
import { ComponentStyle } from 'base/webflow/schema';
import { NodeDecorator } from 'blocks/component_renderer/decorators';
import { ComponentCss } from 'blocks/component_renderer/styles';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import webflowStyles from '../../component_renderer/webflow_base.css';
import { ConceptAPI } from './concept_store';

export const UI_STYLES = [
  UiStyle.Default,
  UiStyle.Bubble,
  UiStyle.Brick,
  UiStyle.Gradient,
  UiStyle.Sleek,
  UiStyle.Elevate,
];

export const getUiStyleLabel = (uiStyle: UiStyle) => {
  switch (uiStyle) {
    case UiStyle.Default:
      return 'Default';
    case UiStyle.Bubble:
      return 'Bubble';
    case UiStyle.Brick:
      return 'Brick';
    case UiStyle.Gradient:
      return 'Gradient';
    case UiStyle.Sleek:
      return 'Sleek';
    case UiStyle.Elevate:
      return 'Elevate';
    default:
      throw new UnreachableError(uiStyle);
  }
};

export const UI_STYLE_RADIUS_OPTIONS = [
  UiStyleRadiusOption.Radius0,
  UiStyleRadiusOption.Radius1,
  UiStyleRadiusOption.Radius2,
  UiStyleRadiusOption.Radius3,
];

export const getUiStyleRadiusOptionIcon = (option: UiStyleRadiusOption) => {
  switch (option) {
    case UiStyleRadiusOption.Radius0:
      return IconRadius0;
    case UiStyleRadiusOption.Radius1:
      return IconRadius1;
    case UiStyleRadiusOption.Radius2:
      return IconRadius2;
    case UiStyleRadiusOption.Radius3:
      return IconRadius3;
    default:
      throw new UnreachableError(option);
  }
};

const mapStyleLessColorVariables = (
  styleLess: string,
  {
    removeTransitions,
    reverseNeutrals,
    overrides = {},
  }: {
    removeTransitions: boolean;
    reverseNeutrals: boolean;
    overrides?: Record<string, string>;
  },
) => {
  const propertyList = styleLess
    .split(';')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [key, value] = line.split(':').map((part) => part.trim());

      if (removeTransitions && key === 'transition') return;

      if (key in overrides) {
        return [key, overrides[key]] as const;
      }

      const newValue = value.replaceAll(
        /@var_relume-variable--([\w\d-]+)/g,
        (original, variable) => {
          if (variable === 'transparent') return `var(--transparent)`;

          if (reverseNeutrals) {
            variable = reverseNeutralColorVariable(variable);
          }

          if (
            variable.match(/^(neutral|chromatic\d+)-(shade-\d+|white)(-\d+)?$/)
          ) {
            return `var(--${variable})`;
          }

          return original;
        },
      );

      return [key, newValue] as const;
    })
    .filter(exists);

  return propertyList.map(([key, value]) => `${key}: ${value};`).join('\n');
};

export const UiStyleCss = observer(
  ({
    uiStyle,
    cardStyle,
    radiusOption,
    library,
    rootClassName,
    removeTransitions = true,
    concept,
  }: {
    uiStyle: UiStyle;
    cardStyle: CardStyle;
    radiusOption: UiStyleRadiusOption;
    library: LibraryCache;
    rootClassName: string;
    removeTransitions?: boolean;
    concept: ConceptAPI;
  }) => {
    const payload = library.getCachedPublicComponentJson(
      getUiStyleComponentSlug(uiStyle, cardStyle),
      {
        fetchIfMissing: true,
      },
    )?.value?.payload;
    if (!payload) return null;

    const styleTree = toNested(payload.styles);

    const re = new RegExp(`^(.*?)-${uiStyle}_${cardStyle}(_.*)?$`);
    const subtrees = styleTree
      .map((node) => {
        const m = node.name.match(re);
        if (!m) return null;

        const [_, base, suffix] = m;
        if (!UI_STYLE_STYLED_CLASSES.has(base)) return null;

        return { ...node, name: `${base}-styled${suffix ?? ''}` };
      })
      .filter(exists);

    const { darkText, darkAlternate } = getPrimaryButtonStyling({
      concept,
      uiStyle,
    });

    const mapStyle = (
      style: Nested<ComponentStyle>,
      prevClassNames: string[] = [],
    ): Nested<ComponentStyle> => {
      const classNames = [...prevClassNames, style.name];

      const isPrimaryButton =
        classNames[0] === 'button-styled' &&
        !(
          classNames.includes('is-secondary') ||
          classNames.includes('is-tertiary') ||
          classNames.includes('is-link')
        );
      const isAlternateButton = classNames.includes('is-alternate');

      const reverseNeutrals =
        darkAlternate && isPrimaryButton && isAlternateButton;

      const overrides: Record<string, string> =
        darkText && isPrimaryButton && !isAlternateButton
          ? {
              color: 'var(--neutral-shade-7)',
            }
          : {};

      return {
        ...style,
        children: (style.children as Nested<ComponentStyle>[]).map((child) =>
          mapStyle(child, classNames),
        ) as typeof style.children,
        styleLess: mapStyleLessColorVariables(style.styleLess, {
          removeTransitions,
          reverseNeutrals,
          overrides,
        }),
        variants: Object.fromEntries(
          Object.entries(style.variants).map(([k, v]) => [
            k,
            {
              styleLess: mapStyleLessColorVariables(v.styleLess, {
                removeTransitions,
                reverseNeutrals,
                overrides,
              }),
            },
          ]),
        ),
      };
    };

    const styles = fromNested(subtrees.map((root) => mapStyle(root)));

    const radiusValues = Object.entries({
      ...UI_STYLE_RADIUS_PIXELS[radiusOption],
      ...UI_STYLE_RADIUS_OVERRIDES_PIXELS[uiStyle],
    })
      .map(([className, value]) => {
        const m = className.match(/^(.*?)(_.*)?$/);
        if (!m) return;

        const [_, base, suffix] = m;
        return [`U_${base}-styled${suffix ?? ''}`, value] as const;
      })
      .filter(exists);

    return (
      <>
        <ComponentCss
          styles={styles}
          assets={payload.assets}
          rootClassName={rootClassName}
        />
        <style>
          {radiusValues.map(
            ([className, value]) =>
              `.${rootClassName} .${className} { border-radius: ${value}px; }`,
          )}
        </style>
      </>
    );
  },
);

export function getUiStyleDecorator(
  scheme: CompiledScheme | null,
): NodeDecorator {
  return () => {
    return [
      {
        replaceClassNames: (classNames) => {
          let newClassNames = classNames;

          newClassNames = newClassNames.map((c) => {
            const m = typeof c === 'string' ? c.match(/^U_(.*?)(_.*)?$/) : null;
            if (m) {
              const [_, base, suffix] = m;
              const name = `${base}${suffix ?? ''}`;
              if (UI_STYLE_STYLED_CLASSES.has(name)) {
                return `U_${base}-styled${suffix ?? ''}`;
              }
            }

            return c;
          });

          const isAlternate =
            scheme &&
            shouldStyleBeAlternate(
              scheme,
              classNames
                .map((c) =>
                  typeof c === 'string' ? c.replace(/^U_/, '') : null,
                )
                .filter(exists),
            );

          if (isAlternate) {
            newClassNames = [...newClassNames, 'U_is-alternate'];
          } else {
            newClassNames = newClassNames.filter((c) => c !== 'U_is-alternate');
          }

          // Ensure the variant style is applied too when the "current" class is present
          if (
            newClassNames.includes('U_category-filter-link-styled') &&
            newClassNames.includes('U_current')
          ) {
            newClassNames = [...newClassNames, webflowStyles['w--current']];
          }

          return newClassNames;
        },
      },
    ];
  };
}

export const globalUiStyleOverrideStore = observable.box<UiStyle | null>(null);
